import { buildSlug } from '@listening/utils/slug-utils';
import { cn } from '@listening/utils/react-helpers';
import { TooltipProvider } from '@listening/frontend/components/radix/tooltip';
import {
  isReadAlongPdfAvailable,
  useAudioPrefsStore,
} from '@listening/frontend/stores/audio-preferences-store';
import { useFlag } from '@listening/frontend/analytics/client';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { resolvePublicDocCategory } from '@listening/api-contract';
import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import { excelSerialToISODate } from '@/lib/excel-serial-to-iso-date';
import { Toaster } from '@listening/frontend/components/radix/sonner';
import { PlayBar } from '@/components/start/play-bar/play-bar';
import { DocsReadAlongPage } from '@/components/astro/features/public-pdf-doc/docs-read-along-page';
import {
  AudioChunkPlayerProvider,
  useAudioChunkPlayer,
  useAudioChunkSelector,
} from '@/lib/audio-chunk-player';
import { clientEnv } from '@/env';
import { seo } from '@/seo/seo';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import { getRuntime } from '@/runtimes/get-runtime';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import { useAtomValue } from '@effect/atom-react';
import {
  audioDetailAtom,
  audioSummaryAtom,
} from '@listening/frontend/query/audio-detail';
import {
  type ArgsOf,
  type LoadedHandle,
  Loader,
  type Source,
} from 'raf-effect-atom';
import {
  successOrThrow,
  useLoadedValue,
} from '@listening/frontend/query/route-loader';
import { Duration, Effect, Layer, Result, Schema } from 'effect';
import { AsyncResult, AtomRegistry } from 'effect/reactivity';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { CHAPTER_CHUNK_SIZE } from '@/lib/doc-reader/core';
import { legacyChapterChunkReader } from '@/lib/doc-reader/legacy-chapter-chunks';
import {
  docPackCatalogAtom,
  docPackPageAtom,
} from '@listening/frontend/doc-pack/source';
import { DOC_PACK_CONTENT_VERSION } from '@listening/doc-pack';
import {
  PublicDocListPage,
  PublicDocListPending,
  publicDocsPageAtom,
  getPublicDocCategoryDescription,
  getPublicDocCategoryFromSlug,
  getPublicDocCategoryHref,
  getPublicDocListHref,
  publicDocSearchSchema,
} from '@/components/astro/features/public-pdf-doc/public-doc-list-page';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';
import { PublicDocTutorial } from '@/components/astro/features/public-pdf-doc/public-doc-tutorial';
import { useEffect, useRef, useState } from 'react';

const siteUrl = clientEnv.siteUrl.replace(/\/$/, '');

// The document branch of the route loader as one Effect program, run on the
// isomorphic runtime at the loader boundary. Succeeds with `null` for the
// expected not-found case (no content from any source) — handled HERE, where
// it's produced, so the loader boundary doesn't need a blanket catchTag that
// would silently 404 on any future NotFoundError leaking from this program.
// The boundary maps `null` to the router's notFound(). (GetDocMetadata never
// 404s by contract: it always returns `{ audioId, doc: nullable }`.)
const ensureValue = <S extends Source>(source: S, ...args: ArgsOf<S>) =>
  Loader.ensure(source, ...args).pipe(
    Effect.flatMap((handle) => {
      const loaded = Loader.result(handle);
      return AsyncResult.isSuccess(loaded)
        ? Effect.succeed(loaded.value)
        : Effect.failCause(loaded.cause);
    }),
  );

const loadPublicDoc = Effect.fn('docIdRoute.loadPublicDoc')(function* (
  docId: string,
) {
  const api = yield* ApiClient;
  const atoms = yield* AtomRegistry.AtomRegistry;

  const result = yield* api.PublicDoc.GetDocMetadata({
    params: { slug: docId },
  });

  // Intentionally degrades to null (metadata fallback) on 404 instead of
  // 404ing the route.
  const fetchDbDoc = api.PublicDoc.GetPublicDoc({
    params: { publicId: result.audioId },
  }).pipe(Effect.catchTag('NotFoundError', () => Effect.succeed(null)));

  const [audioItemResult, audioSummaryResult, dbDoc] = yield* Effect.all(
    [
      Effect.result(ensureValue(audioDetailAtom, result.audioId)),
      Effect.result(ensureValue(audioSummaryAtom, result.audioId)),
      fetchDbDoc,
    ],
    { concurrency: 'unbounded' },
  );

  if (!dbDoc && !result.doc) {
    yield* Effect.logDebug(`No content found for public doc ${docId}`);
    return null;
  }

  // The runtime reader reads the audio by its canonical id (from
  // audioItemDetail.id). result.audioId is the public-doc slug/id, which is
  // often different. We do two things so the reader starts warm:
  // (1) Write the audio detail under the canonical id too, so
  //     useDocumentSummary's read of the canonical id hits.
  // (2) Prefetch chapters using the canonical id (the same key the reader
  //     derives from documentSummary.id).
  const canonicalAudioId = Result.isSuccess(audioItemResult)
    ? audioItemResult.success.id
    : result.audioId;

  if (
    Result.isSuccess(audioItemResult) &&
    canonicalAudioId !== result.audioId
  ) {
    atoms.set(audioDetailAtom(canonicalAudioId), audioItemResult.success);
  }

  // Prefetch the first reader payload into the registry, so it travels with
  // the page.
  //
  // The legacy chapter chunk-0 is prefetched for EVERY doc — including doc-pack-backed
  // ones. The SSR text reader (ReaderViewTanStack) is mounted server-side for
  // SEO on all docs and only swaps to the doc-pack reader AFTER hydration, so without
  // chunk-0 an doc-pack-backed doc SSRs with no chapter text (broken crawler content
  // + the public-doc-perf SSR lock-in test). Previously this was an
  // `doc-pack ? catalog : chunk-0` either/or, which silently dropped SSR text the
  // moment a doc became doc-pack-backed.
  // doc-pack docs additionally hydrate the doc-pack catalog + first page for the
  // post-hydration doc-pack reader; no-doc-pack doc-pack docs prefetch the doc-pack catalog
  // to drop the detail→catalog round-trip the PDF view would pay after hydration
  // (page blobs stay client-fetched).
  // Best-effort: failures are logged at debug level and the client refetches
  // on hydration.
  if (Result.isSuccess(audioItemResult)) {
    const docPack =
      audioItemResult.success.doc_pack?.content_version ===
      DOC_PACK_CONTENT_VERSION
        ? audioItemResult.success.doc_pack
        : null;
    yield* Effect.all(
      [
        ensureValue(legacyChapterChunkReader.chunkSource, {
          cursor: '',
          limit: CHAPTER_CHUNK_SIZE,
          audioId: canonicalAudioId,
        }).pipe(Effect.ignore({ log: true })),
        docPack
          ? Effect.gen(function* () {
              const loadedPack = yield* ensureValue(docPackCatalogAtom, {
                audioId: canonicalAudioId,
                url: docPack.url,
              });
              const firstPage = loadedPack.catalog.pages[0];
              if (firstPage) {
                yield* ensureValue(docPackPageAtom, {
                  audioId: canonicalAudioId,
                  packUrl: loadedPack.url,
                  page: firstPage.n,
                  // The page reader's mode on web, so the reader hydrates
                  // this very page.
                  fetchMode: 'auto',
                });
              }
            }).pipe(Effect.ignore({ log: true }))
          : Effect.void,
      ],
      { concurrency: 'unbounded' },
    );
  }

  const title =
    result.doc?.metadata.title ??
    dbDoc?.title ??
    (Result.isSuccess(audioItemResult)
      ? audioItemResult.success.title
      : null) ??
    (Result.isSuccess(audioSummaryResult)
      ? audioSummaryResult.success.title
      : null) ??
    '';
  const relatedDocs = yield* getRelatedPublicDocs({
    currentPublicId: result.audioId,
    category:
      dbDoc?.category ??
      resolvePublicDocCategory(result.doc?.metadata.category ?? null),
  });
  const canonical = normalizeCanonicalUrl(
    `${siteUrl}/doc/${buildSlug(title || null, result.audioId)}`,
  );

  return {
    type: 'document' as const,
    ...result,
    dbDoc,
    relatedDocs,
    title,
    canonical,
  };
});

function toPublicDocAbsoluteUrl(pathOrUrl: string) {
  if (/^https?:\/\//.test(pathOrUrl)) return normalizeCanonicalUrl(pathOrUrl);
  return normalizeCanonicalUrl(`${siteUrl}${pathOrUrl}`);
}

function publicDocBreadcrumbJsonLd(
  items: ReadonlyArray<{ name: string; href: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toPublicDocAbsoluteUrl(item.href),
    })),
  };
}

const docIdRouteAnalytics = experimentalRoutes['/_marketing/doc/$docId'];

const PUBLIC_DOC_CDN_MAX_AGE = Duration.minutes(5);
const PUBLIC_DOC_CACHE_CONTROL = `public, max-age=0, s-maxage=${Duration.toSeconds(
  PUBLIC_DOC_CDN_MAX_AGE,
).toString()}`;

export const Route = createFileRoute('/_marketing/doc/$docId')({
  validateSearch: Schema.toStandardSchemaV1(publicDocSearchSchema),
  staticData: { analytics: docIdRouteAnalytics },
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    cursor: search.cursor,
    search: search.search,
  }),
  remountDeps: ({ params }) =>
    getPublicDocCategoryFromSlug(params.docId) ? 'category' : params.docId,
  headers: cacheHeadersFor(docIdRouteAnalytics, PUBLIC_DOC_CACHE_CONTROL),
  shouldReload: () => true,
  loader: async ({ params, context, deps, abortController }) => {
    const selectedCategory = getPublicDocCategoryFromSlug(params.docId);
    if (selectedCategory) {
      const basePath = getPublicDocCategoryHref(selectedCategory);

      const publicDocsPage = await context.atoms.ensure(publicDocsPageAtom, {
        page: deps.page,
        cursor: deps.cursor ?? null,
        search: deps.search ?? null,
        category: selectedCategory,
      });

      return {
        type: 'category' as const,
        selectedCategory,
        publicDocsPage,
        canonical: getPublicDocListHref(basePath, {
          page: successOrThrow(publicDocsPage).page,
        }),
        search: deps.search,
      };
    }

    const loaded = await context.atoms.run(
      loadPublicDoc(params.docId).pipe(
        Effect.provide(Layer.effectContext(getRuntime().contextEffect)),
      ),
      { signal: abortController.signal },
    );
    if (loaded === null) throw notFound();
    return loaded;
  },
  head: ({ loaderData }) => {
    if (loaderData?.type === 'category') {
      const titleSubject = loaderData.selectedCategory;
      const description = getPublicDocCategoryDescription(
        loaderData.selectedCategory,
      );
      const canonical = toPublicDocAbsoluteUrl(loaderData.canonical);
      const collectionJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${titleSubject} Documents`,
        description,
        url: canonical,
      };
      const breadcrumbJsonLd = publicDocBreadcrumbJsonLd([
        { name: 'Home', href: '/' },
        { name: 'Documents', href: '/doc' },
        {
          name: loaderData.selectedCategory,
          href: getPublicDocCategoryHref(loaderData.selectedCategory),
        },
      ]);

      return {
        meta: [
          ...seo({
            title: `${titleSubject} Documents | Listening`,
            description,
          }),
          {
            name: 'robots',
            content: loaderData.search ? 'noindex, follow' : 'index, follow',
          },
        ],
        links: [{ rel: 'canonical', href: canonical }],
        scripts: [
          {
            type: 'application/ld+json',
            children: JSON.stringify(collectionJsonLd),
          },
          {
            type: 'application/ld+json',
            children: JSON.stringify(breadcrumbJsonLd),
          },
        ],
      };
    }

    const title = loaderData?.title ?? 'Public Document';
    const jsonDoc = loaderData?.doc;
    const dbDoc = loaderData?.dbDoc;
    const coverImg = jsonDoc?.audioConversion.cover_img ?? dbDoc?.coverImageUrl;

    const description = buildDocMetaDescription(title, {
      summary: dbDoc?.summary,
      abstract: jsonDoc?.metadata.abstract,
      description: dbDoc?.description,
    });

    const authorName = jsonDoc?.metadata.authors ?? dbDoc?.publishingAuthor;
    const documentCategory = jsonDoc?.metadata.category;
    const mainCategory =
      dbDoc?.category ?? resolvePublicDocCategory(documentCategory ?? null);
    const breadcrumbItems = [
      { name: 'Home', href: '/' },
      { name: 'Documents', href: '/doc' },
      ...(mainCategory && mainCategory !== 'Other'
        ? [{ name: mainCategory, href: getPublicDocCategoryHref(mainCategory) }]
        : []),
      {
        name: title || 'Public Document',
        href: loaderData?.canonical ?? `/doc/${loaderData?.audioId ?? ''}`,
      },
    ];

    const articleJsonLd = (() => {
      if (!title) return undefined;
      const isScholarly = Boolean(jsonDoc?.metadata);
      const jsonLd: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': isScholarly ? 'ScholarlyArticle' : 'Article',
        headline: title,
        description:
          jsonDoc?.metadata.abstract || dbDoc?.description || undefined,
      };

      if (authorName) {
        jsonLd.author = authorName.split(',').map((name: string) => ({
          '@type': 'Person',
          name: name.trim(),
        }));
      }

      if (jsonDoc?.metadata.source) {
        jsonLd.publisher = {
          '@type': 'Organization',
          name: jsonDoc.metadata.source,
        };
      }

      if (jsonDoc?.metadata) {
        const isoDate = excelSerialToISODate(jsonDoc.metadata.publication_date);
        if (isoDate) jsonLd.datePublished = isoDate;
        if (jsonDoc.metadata.citation_count)
          jsonLd.citationCount = jsonDoc.metadata.citation_count;
        if (jsonDoc.metadata.copyright_text)
          jsonLd.copyrightNotice = jsonDoc.metadata.copyright_text;
      } else if (dbDoc?.publishingYear) {
        jsonLd.datePublished = `${dbDoc.publishingYear}`;
      }

      if (coverImg) jsonLd.image = coverImg;

      jsonLd.associatedMedia = {
        '@type': 'AudioObject',
        name: `${title} (Audio)`,
        description: `AI-generated audiobook version of "${title}"`,
      };

      return jsonLd;
    })();

    const scripts: Array<{ type: string; children: string }> = [
      {
        type: 'application/ld+json',
        children: JSON.stringify(publicDocBreadcrumbJsonLd(breadcrumbItems)),
      },
    ];
    if (articleJsonLd) {
      scripts.push({
        type: 'application/ld+json',
        children: JSON.stringify(articleJsonLd),
      });
    }

    return {
      meta: [
        ...seo({
          title: `${title} | Listening`,
          description,
          image: coverImg ?? undefined,
        }),
      ],
      links: loaderData?.canonical
        ? [{ rel: 'canonical', href: loaderData.canonical }]
        : undefined,
      scripts,
    };
  },
  pendingComponent: () => <PublicDocListPending />,
  pendingMs: 0,
  component: Page,
});

function buildDocMetaDescription(
  title: string,
  opts: {
    summary?: string | null | undefined;
    abstract?: string | null | undefined;
    description?: string | null | undefined;
  },
) {
  const fallback = `Listen to "${title}" on Listening.com — AI-powered text-to-speech with accurate pronunciation. Free audiobook.`;

  const raw = opts.summary ?? opts.description ?? opts.abstract;
  if (!raw) return fallback;

  const cleaned = raw
    .replaceAll(/\n+/g, ' ')
    .replace(
      /^(this\s+(document|paper|study|article|report|research|review|analysis)\s+(provides|presents|examines|explores|discusses|outlines|analyzes|investigates|aims|describes|covers|focuses on)\s+(a\s+|an\s+|the\s+)?((comprehensive|detailed|brief|systematic|thorough|general|complete)\s+(overview|analysis|review|examination|summary|discussion)\s+(of\s+)?)?)/i,
      '',
    )
    .replace(/^\s+/, '');

  const content = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

  const prefix = `Listen to "${title}" — `;
  const suffix = '. Free on Listening.com';
  const budget = 160 - prefix.length - suffix.length;

  if (budget < 20) return fallback;

  const truncated =
    content.length <= budget
      ? content.replace(/\.$/, '')
      : `${content.slice(0, budget).replace(/\s+\S*$/, '')}...`;

  return `${prefix}${truncated}${suffix}`;
}

type PublicDocDbData = {
  publicId: string;
  slug: string;
  title: string | null;
  description: string | null;
  summary: string | null;
  keyPoints: string | null;
  category: string | null;
  coverImageUrl: string | null;
  publishingAuthor: string | null;
  publishingYear: number | null;
};

type RelatedPublicDoc = {
  publicId: string;
  slug: string;
  title: string | null;
  description: string | null;
  category: string | null;
};

const getRelatedPublicDocs = Effect.fn('docIdRoute.getRelatedPublicDocs')(
  function* ({
    currentPublicId,
    category,
  }: {
    currentPublicId: string;
    category: string | null | undefined;
  }) {
    if (!category) return [];

    const api = yield* ApiClient;
    const result = yield* Effect.result(
      api.PublicDoc.ListPublicDocs({
        query: {
          category,
          limit: 12,
          page: 1,
        },
      }),
    );

    if (Result.isFailure(result)) {
      // Related docs are decorative — degrade to none rather than fail the
      // loader, but never silently.
      yield* Effect.logWarning(
        'related public docs fetch failed',
        result.failure,
      );
      return [];
    }

    return result.success.items
      .filter((item) => item.publicId !== currentPublicId)
      .slice(0, 5);
  },
);

function Page() {
  const loaderData = Route.useLoaderData();

  if (loaderData.type === 'category') {
    return (
      <PublicDocCategoryPage
        selectedCategory={loaderData.selectedCategory}
        publicDocsPage={loaderData.publicDocsPage}
      />
    );
  }

  return <PublicDocPage {...loaderData} />;
}

function PublicDocPage({
  title,
  audioId,
  doc,
  dbDoc,
  relatedDocs,
}: NonNullable<Effect.Success<ReturnType<typeof loadPublicDoc>>>) {
  const docFullscreen = useAudioPrefsStore((s) => s.docFullscreen);
  const { value: overviewPopupVariant } = useFlag(
    'marketingPublicDocOverviewPopupV1',
  );
  const [mobileReaderOpen, setMobileReaderOpen] = useState(false);
  const audioItem = AsyncResult.getOrElse(
    useAtomValue(audioDetailAtom(audioId)),
    () => undefined,
  );
  const audioSummary = AsyncResult.getOrElse(
    useAtomValue(audioSummaryAtom(audioId)),
    () => null,
  );

  if (!audioItem)
    return (
      <PublicDocMetadataFallback
        title={title}
        doc={doc}
        dbDoc={dbDoc}
        audioSummary={audioSummary}
        relatedDocs={relatedDocs}
      />
    );

  const showMobileDocumentLanding =
    overviewPopupVariant === 'treatment' &&
    isReadAlongPdfAvailable(audioItem).isPdf &&
    !mobileReaderOpen;
  const effectiveDocFullscreen = docFullscreen && !showMobileDocumentLanding;

  return (
    <TooltipProvider>
      <AudioChunkPlayerProvider
        initialAudioId={audioItem.id}
        initialLookupAudioId={audioId}
      >
        <PublicDocInitialChunkLoader lookupAudioId={audioId} />
        <div className='h-svh w-full overflow-hidden bg-white dark:bg-[#0a0e1a]'>
          <PublicDocTutorial />
          <div className='flex size-full flex-col'>
            <div className='flex grow overflow-hidden'>
              <main className='h-full grow overflow-hidden'>
                <DocsReadAlongPage
                  slug={audioId}
                  title={title}
                  dbDoc={dbDoc}
                  experimentNames={docIdRouteAnalytics.experiments}
                  onMobileReaderOpenChange={setMobileReaderOpen}
                  overviewPopupVariant={overviewPopupVariant}
                  showMobileDocumentLanding={showMobileDocumentLanding}
                  relatedDocs={relatedDocs}
                  storeProps={{
                    metadata: doc?.metadata,
                  }}
                />
              </main>
              <Toaster />
            </div>
            <div
              className={cn('dark', effectiveDocFullscreen && 'max-sm:hidden')}
            >
              <PlayBar className='shadow-none' />
            </div>
          </div>
        </div>
      </AudioChunkPlayerProvider>
    </TooltipProvider>
  );
}

function PublicDocInitialChunkLoader({
  lookupAudioId,
}: {
  lookupAudioId: string;
}) {
  const { loadAudio } = useAudioChunkPlayer();
  const ttsId = useAudioChunkSelector((s) => s.ttsId);
  const docPackChunk = useAudioChunkSelector((s) => s.docPackChunk);
  const didLoadRef = useRef(false);

  useEffect(() => {
    if (ttsId || docPackChunk || didLoadRef.current) return;
    didLoadRef.current = true;
    loadAudio(lookupAudioId, undefined, undefined, {
      loadPos: { posMs: 0 },
      autoPlay: false,
    });
  }, [docPackChunk, loadAudio, lookupAudioId, ttsId]);

  return null;
}

function PublicDocCategoryPage({
  selectedCategory,
  publicDocsPage,
}: {
  selectedCategory: string;
  publicDocsPage: LoadedHandle<typeof publicDocsPageAtom>;
}) {
  const data = useLoadedValue(publicDocsPageAtom, publicDocsPage);
  const { docId } = Route.useParams();
  const { search } = Route.useSearch();
  const currentCategory =
    getPublicDocCategoryFromSlug(docId) ?? selectedCategory;
  const basePath = getPublicDocCategoryHref(currentCategory);

  return (
    <PublicDocListPage
      data={data}
      selectedCategory={currentCategory}
      search={search}
      basePath={basePath}
    />
  );
}

function RelatedPublicDocs({
  relatedDocs,
}: {
  relatedDocs: ReadonlyArray<RelatedPublicDoc>;
}) {
  if (relatedDocs.length === 0) return null;

  return (
    <section className='space-y-3'>
      <h2 className='text-[11px] font-semibold tracking-[0.08em] text-[#94A4B5] uppercase'>
        Related articles
      </h2>
      <ul className='grid gap-3 sm:grid-cols-2'>
        {relatedDocs.map((relatedDoc) => (
          <li key={relatedDoc.publicId}>
            <a
              href={normalizeCanonicalUrl(`/doc/${relatedDoc.slug}`)}
              className='block rounded-2xl border border-[#E2E8EE] bg-white p-4 transition hover:border-[#1E7FBF]/40 hover:shadow-[0_2px_12px_rgba(3,49,75,0.06)]'
            >
              <h3 className='text-sm leading-5 font-semibold text-[#03314B]'>
                {relatedDoc.title || 'Untitled document'}
              </h3>
              {relatedDoc.description && (
                <p className='mt-2 line-clamp-2 text-sm leading-6 text-[#5A7387]'>
                  {relatedDoc.description}
                </p>
              )}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PublicDocMetadataFallback({
  title,
  doc,
  dbDoc,
  audioSummary,
  relatedDocs,
}: {
  title: string;
  doc: {
    metadata: {
      abstract: string;
      authors: string;
      category?: string | undefined;
      source: string;
    };
    audioConversion: {
      cover_img?: string | undefined;
    };
  } | null;
  dbDoc: PublicDocDbData | null;
  audioSummary: AudioItemSummary | null;
  relatedDocs: ReadonlyArray<RelatedPublicDoc>;
}) {
  const summary =
    doc?.metadata.abstract ?? dbDoc?.summary ?? dbDoc?.description;
  const authors = doc?.metadata.authors ?? dbDoc?.publishingAuthor;
  const sourceUrl = audioSummary?.document_url ?? null;
  const source = doc?.metadata.source ?? sourceUrl;
  const category =
    dbDoc?.category ??
    (doc?.metadata.category
      ? resolvePublicDocCategory(doc.metadata.category)
      : null);
  const coverImg = doc?.audioConversion.cover_img ?? dbDoc?.coverImageUrl;
  const keyPointsList = dbDoc?.keyPoints
    ?.split(' | ')
    .map((point: string) => point.trim())
    .filter(Boolean);
  const metadataItems = [
    authors ? { label: 'Authors', value: authors } : null,
    source ? { label: 'Source', value: source } : null,
    category ? { label: 'Category', value: category } : null,
    audioSummary?.page_count
      ? { label: 'Pages', value: `${audioSummary.page_count}` }
      : null,
  ].filter(
    (
      item,
    ): item is {
      label: string;
      value: string;
    } => item !== null,
  );
  const eyebrow = category?.split(',')[0]?.trim() ?? 'Document';

  return (
    <>
      <NavBar sticky={false} />

      <main className='mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-10 md:px-6 md:py-14'>
        <section className='grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start'>
          <article className='max-w-[680px] space-y-9'>
            <header className='space-y-4'>
              <div className='text-[12px] font-semibold tracking-[0.08em] text-[#1E7FBF] uppercase'>
                {eyebrow}
              </div>
              <h1 className='font-serif text-4xl leading-[1.18] font-medium tracking-[-0.01em] text-[#03314B] md:text-5xl'>
                {title || 'Public Document'}
              </h1>
              {authors && (
                <div className='text-[13.5px] text-[#5A7387]'>{authors}</div>
              )}
              {audioSummary?.page_count && (
                <div className='text-[12.5px] text-[#94A4B5]'>
                  {audioSummary.page_count} pages
                </div>
              )}
            </header>

            <div className='flex flex-wrap gap-2'>
              {sourceUrl && (
                <a
                  href={sourceUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='inline-flex h-9 items-center gap-2 rounded-full border border-[#E2E8EE] px-4 text-[13px] font-medium text-[#1F4A66] transition hover:border-[#1E7FBF]/40 hover:text-[#03314B]'
                >
                  Open source document
                </a>
              )}
            </div>

            <div className='border-t border-[#E2E8EE]' />

            <div className='rounded-xl border border-[#E1EEF8] bg-[#F5F8FB]/60 p-4 text-sm leading-6 text-[#1F4A66]'>
              This public document is listed correctly and its metadata is
              available, but the full reader content is currently unavailable
              from the document detail API. The metadata we do have is shown
              below.
            </div>

            {summary && (
              <section className='space-y-3'>
                <h2 className='text-[11px] font-semibold tracking-[0.08em] text-[#94A4B5] uppercase'>
                  Summary
                </h2>
                <p className='text-[17px] leading-[1.65] text-[#1F4A66]'>
                  {summary}
                </p>
              </section>
            )}

            {keyPointsList && keyPointsList.length > 0 && (
              <section className='space-y-3'>
                <h2 className='text-[11px] font-semibold tracking-[0.08em] text-[#94A4B5] uppercase'>
                  Key Points
                </h2>
                <ul className='space-y-3 text-[17px] leading-[1.65] text-[#1F4A66]'>
                  {keyPointsList.map((point: string, i: number) => (
                    <li className='flex gap-3' key={point}>
                      <span className='mt-1 grid size-[18px] shrink-0 place-items-center rounded-full bg-[#E1EEF8] text-[10px] font-semibold text-[#0E5A8E]'>
                        {i + 1}
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <RelatedPublicDocs relatedDocs={relatedDocs} />
          </article>

          <aside className='space-y-5 rounded-3xl border border-[#E2E8EE] bg-white p-6 shadow-[0_2px_12px_rgba(3,49,75,0.04)]'>
            {coverImg && (
              <img
                alt={title || 'Document cover'}
                className='w-full rounded-2xl border border-[#E2E8EE] object-cover'
                src={coverImg}
              />
            )}

            {metadataItems.length > 0 && (
              <dl className='space-y-4'>
                {metadataItems.map((item) => (
                  <div key={item.label}>
                    <dt className='text-[11px] font-semibold tracking-[0.08em] text-[#94A4B5] uppercase'>
                      {item.label}
                    </dt>
                    <dd className='mt-1 text-sm leading-6 text-[#03314B]'>
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </aside>
        </section>
      </main>

      <Footer />
    </>
  );
}
